/**
 * Leckerorder Design Tokens — Frontend Translation Layer
 *
 * Single source of truth for all design-system-to-Tailwind mappings.
 * Mirrors the design system's semantic tokens (design-system/tokens/semantic-colors.json).
 *
 * Components and utils import from here — never hardcode Tailwind color classes
 * for statuses, payments, or confirmations elsewhere.
 *
 * If the design system changes, update this ONE file.
 */

import type { LucideIcon } from 'lucide-react'
import {
  Clock,
  Inbox,
  CheckCircle2,
  ChefHat,
  Package,
  CircleCheckBig,
  XCircle,
  Circle,
  MessageCircle,
  X,
  Check,
} from 'lucide-react'

// ── Types ────────────────────────────────────────────────────────

export interface StatusStyle {
  bg: string
  text: string
  border: string
}

export interface PaymentStyle {
  bg: string
  text: string
}

export interface ConfirmationStyle {
  bg: string
  text: string
  border: string
  icon: LucideIcon
}

export interface UrgencyThreshold {
  maxMinutes: number
  className: string
}

// ── Order Status → Tailwind Classes ──────────────────────────────

export const ORDER_STATUS_STYLES: Record<string, StatusStyle> = {
  pending:   { bg: 'bg-status-warning/15', text: 'text-status-warning', border: 'border-status-warning/30' },
  received:  { bg: 'bg-status-info/15',    text: 'text-status-info',    border: 'border-status-info/30' },
  confirmed: { bg: 'bg-status-success/15', text: 'text-status-success', border: 'border-status-success/30' },
  preparing: { bg: 'bg-status-warning/15', text: 'text-status-warning', border: 'border-status-warning/30' },
  ready:     { bg: 'bg-status-success/15', text: 'text-status-success', border: 'border-status-success/30' },
  completed: { bg: 'bg-status-neutral/15', text: 'text-status-neutral', border: 'border-status-neutral/30' },
  cancelled: { bg: 'bg-status-error/15',   text: 'text-status-error',   border: 'border-status-error/30' },
}

const DEFAULT_STATUS_STYLE: StatusStyle = ORDER_STATUS_STYLES.cancelled

export function getOrderStatusStyle(status: string, isCancelled = false): StatusStyle {
  if (isCancelled) return ORDER_STATUS_STYLES.cancelled
  return ORDER_STATUS_STYLES[status.toLowerCase()] ?? DEFAULT_STATUS_STYLE
}

// ── Order Status → Icons (for accessibility: color + icon always) ─

export const ORDER_STATUS_ICONS: Record<string, LucideIcon> = {
  pending:   Clock,
  received:  Inbox,
  confirmed: CheckCircle2,
  preparing: ChefHat,
  ready:     Package,
  completed: CircleCheckBig,
  cancelled: XCircle,
}

export const DEFAULT_STATUS_ICON: LucideIcon = Circle

export function getOrderStatusIcon(status: string, isCancelled = false): LucideIcon {
  if (isCancelled) return ORDER_STATUS_ICONS.cancelled
  return ORDER_STATUS_ICONS[status.toLowerCase()] ?? DEFAULT_STATUS_ICON
}

// ── Payment Status → Tailwind Classes ────────────────────────────

export const PAYMENT_STATUS_STYLES: Record<string, PaymentStyle> = {
  cash:            { bg: 'bg-status-warning/15', text: 'text-status-warning' },
  paypal_success:  { bg: 'bg-status-success/15', text: 'text-status-success' },
  wirecard_success:{ bg: 'bg-status-success/15', text: 'text-status-success' },
  cc_success:      { bg: 'bg-status-success/15', text: 'text-status-success' },
  stripe_success:  { bg: 'bg-status-success/15', text: 'text-status-success' },
  paypalme_new:    { bg: 'bg-status-success/15', text: 'text-status-success' },
  pending:         { bg: 'bg-status-warning/15', text: 'text-status-warning' },
}

const DEFAULT_PAYMENT_STYLE: PaymentStyle = { bg: 'bg-status-neutral/15', text: 'text-status-neutral' }

export function getPaymentStyle(paymentStatus: string): PaymentStyle {
  return PAYMENT_STATUS_STYLES[paymentStatus.toLowerCase()] ?? DEFAULT_PAYMENT_STYLE
}

// ── Refund Status → Tailwind Classes ─────────────────────────────

export const REFUND_STATUS_STYLES: Record<string, PaymentStyle> = {
  succeeded: { bg: 'bg-status-success/15', text: 'text-status-success' },
  failed:    { bg: 'bg-status-error/15',   text: 'text-status-error' },
  pending:   { bg: 'bg-status-warning/15', text: 'text-status-warning' },
}

const DEFAULT_REFUND_STYLE: PaymentStyle = { bg: 'bg-status-neutral/15', text: 'text-status-neutral' }

export function getRefundStyle(refundStatus: string): PaymentStyle {
  return REFUND_STATUS_STYLES[refundStatus.toLowerCase()] ?? DEFAULT_REFUND_STYLE
}

// ── Confirmation Status → Tailwind Classes + Icons ───────────────

export const CONFIRMATION_STATUS_STYLES: Record<string, ConfirmationStyle> = {
  pending:   { bg: 'bg-status-warning/15', text: 'text-status-warning', border: 'border-status-warning/30', icon: Clock },
  replied:   { bg: 'bg-status-info/15',    text: 'text-status-info',    border: 'border-status-info/30',    icon: MessageCircle },
  confirmed: { bg: 'bg-status-success/15', text: 'text-status-success', border: 'border-status-success/30', icon: Check },
  declined:  { bg: 'bg-status-error/15',   text: 'text-status-error',   border: 'border-status-error/30',   icon: X },
}

const DEFAULT_CONFIRMATION_STYLE: ConfirmationStyle = {
  bg: 'bg-status-neutral/15', text: 'text-status-neutral', border: 'border-status-neutral/30', icon: Clock,
}

export function getConfirmationStyle(status: string): ConfirmationStyle {
  return CONFIRMATION_STATUS_STYLES[status] ?? DEFAULT_CONFIRMATION_STYLE
}

// ── Time Urgency → Color Classes ─────────────────────────────────

export const URGENCY_THRESHOLDS: UrgencyThreshold[] = [
  { maxMinutes: 5,  className: 'text-status-success' },
  { maxMinutes: 15, className: 'text-status-warning' },
  { maxMinutes: 30, className: 'text-status-warning' },
]

export const URGENCY_CRITICAL_CLASS = 'text-status-error'

export function getUrgencyClass(minutes: number): string {
  for (const threshold of URGENCY_THRESHOLDS) {
    if (minutes < threshold.maxMinutes) return threshold.className
  }
  return URGENCY_CRITICAL_CLASS
}

// ── Kanban / Card Accent Borders ─────────────────────────────────

export const CARD_ACCENT = {
  cancelled: 'border-s-status-neutral',
  answered:  'border-s-status-success',
  open:      'border-s-status-error',
} as const

// State is signalled primarily by the thick start accent (CARD_ACCENT).
// Only orders that still need attention keep a soft red tint; answered and
// cancelled cards stay on the plain card surface so the list reads calm
// and badges/typography keep their contrast.
export const CARD_BG = {
  cancelled: { bg: 'bg-card', border: 'border-border/70' },
  answered:  { bg: 'bg-card', border: 'border-border/70' },
  open:      { bg: 'bg-status-error/[0.07]', border: 'border-status-error/25' },
} as const

// ── Order Type → Colors ──────────────────────────────────────────

export const ORDER_TYPE_STYLES: Record<string, { color: string }> = {
  delivery: { color: 'text-status-info' },
  pickup:   { color: 'text-status-success' },
  dineIn:   { color: 'text-status-neutral' },
}
